import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import ROUTES from '../config/routes';

const NavigationContext = createContext(null);

const MAIN_ROUTES = [
    ROUTES.HOME,
    ROUTES.ABOUT_US,
    ROUTES.SWING_VISION,
    ROUTES.Q_ALPHA_FLOW,
    ROUTES.OCTOPUS_SYNC,
    ROUTES.SENTINEL,
    ROUTES.CONTACTS,
    ROUTES.NOT_FOUND,
].map(path => (path !== '/' && path.endsWith('/')) ? path.slice(0, -1) : path);

export const NavigationProvider = ({ children }) => {
    const location = useLocation();

    const [expandedMenu, setExpandedMenuState] = useState(null);
    const [userHasToggledMenu, setUserHasToggledMenu] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [activeSection, setActiveSectionState] = useState(null);

    const prevPathnameRef = useRef(location.pathname);

    const getParentPath = (path) => {
        if (!path || path === '/') return '/';
        const segments = path.split('/').filter(Boolean);
        if (segments.length === 0) return '/';
        return `/${segments[0]}`;
    };

    const isMainRoute = useCallback((path) => {
        const normalizedPath = path !== '/' && path.endsWith('/') ? path.slice(0, -1) : path;
        return MAIN_ROUTES.includes(normalizedPath);
    }, []);

    // Auto-expand menu based on current route
    useEffect(() => {
        const currentPathnameNorm = location.pathname !== '/' && location.pathname.endsWith('/') ? location.pathname.slice(0, -1) : location.pathname;
        const currentParentPath = getParentPath(currentPathnameNorm);
        const previousParentPath = getParentPath(prevPathnameRef.current);

        let autoExpandedMenuTarget = null;
        if (currentPathnameNorm === ROUTES.SWING_VISION) autoExpandedMenuTarget = 'SwingVision';
        else if (currentPathnameNorm === ROUTES.Q_ALPHA_FLOW) autoExpandedMenuTarget = 'QAlphaFlow';
        else if (currentPathnameNorm === ROUTES.OCTOPUS_SYNC) autoExpandedMenuTarget = 'Octopus Sync';

        if (currentParentPath !== previousParentPath) {
            setExpandedMenuState(autoExpandedMenuTarget);
            setUserHasToggledMenu(false);
        } else {
            if (!userHasToggledMenu && autoExpandedMenuTarget && expandedMenu !== autoExpandedMenuTarget) {
                setExpandedMenuState(autoExpandedMenuTarget);
            }
        }

        setIsMobileMenuOpen(false);
        prevPathnameRef.current = currentPathnameNorm;
    }, [location.pathname, userHasToggledMenu, expandedMenu]);

    const handleMenuToggle = useCallback((title) => {
        setExpandedMenuState((prev) => {
            const newMenu = prev === title ? null : title;
            setUserHasToggledMenu(true);
            return newMenu;
        });
    }, []);

    const setActiveSection = useCallback((sectionId) => {
        setActiveSectionState(sectionId);
    }, []);

    const value = {
        expandedMenu,
        setExpandedMenu: handleMenuToggle,
        handleMenuToggle,
        userHasToggledMenu,
        isMobileMenuOpen,
        setIsMobileMenuOpen,
        activeSection,
        setActiveSection,
        isMainRoute,
    };

    return (
        <NavigationContext.Provider value={value}>
            {children}
        </NavigationContext.Provider>
    );
};

NavigationProvider.propTypes = {
    children: PropTypes.node.isRequired,
};

export const useNavigation = () => {
    const context = useContext(NavigationContext);
    if (!context) {
        throw new Error('useNavigation must be used within a NavigationProvider');
    }
    return context;
};

export default NavigationContext;